/**
 * Deterministic prequalification readiness from real domain fields.
 * Prefer READY / BLOCKED / IN_PROGRESS — never invent completion percentages.
 */
export type PrequalReadinessState = 'READY' | 'BLOCKED' | 'IN_PROGRESS';

export type PrequalReadinessCheck = {
  id: string;
  group: string;
  label: string;
  state: PrequalReadinessState;
  detail?: string;
};

export type PrequalReadinessGroup = {
  name: string;
  state: PrequalReadinessState;
  checks: PrequalReadinessCheck[];
};

export type PrequalReadinessResult = {
  overall: PrequalReadinessState;
  checks: PrequalReadinessCheck[];
  groups: PrequalReadinessGroup[];
};

const TERMINAL_PASS = new Set(['qualified', 'passed', 'approved']);
const TERMINAL_FAIL = new Set(['failed', 'rejected', 'cancelled', 'expired']);

function asStr(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

function normalizeStatus(status: string): string {
  return status.toLowerCase().replace(/\s+/g, '_');
}

function rollup(states: PrequalReadinessState[]): PrequalReadinessState {
  if (states.some((s) => s === 'BLOCKED')) return 'BLOCKED';
  if (states.length && states.every((s) => s === 'READY')) return 'READY';
  return 'IN_PROGRESS';
}

function parseDueDate(raw: string): Date | null {
  if (!raw) return null;
  const d = new Date(raw.length <= 10 ? `${raw}T00:00:00` : raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Build readiness groups for an `epc_prequalifications` row.
 */
export function buildPrequalReadiness(
  row: Record<string, unknown>,
  opts?: { today?: Date }
): PrequalReadinessResult {
  const today = startOfDay(opts?.today ?? new Date());
  const title = asStr(row.title);
  const client = asStr(row.client_name || row.clientName);
  const opportunityId = asStr(row.opportunity_id || row.opportunityId);
  const dueRaw = asStr(row.due_date || row.dueDate);
  const gate = asStr(row.tender_gate || row.tenderGate || 'NOT_REQUIRED').toUpperCase();
  const statusRaw = asStr(row.status || 'Draft');
  const status = normalizeStatus(statusRaw);
  const isPass = TERMINAL_PASS.has(status);
  const isFail = TERMINAL_FAIL.has(status);

  const checks: PrequalReadinessCheck[] = [];

  checks.push({
    id: 'identity-title',
    group: 'Identity',
    label: 'Title',
    state: title ? 'READY' : 'BLOCKED',
    detail: title ? undefined : 'Title is required',
  });
  checks.push({
    id: 'identity-client',
    group: 'Identity',
    label: 'Client',
    state: client ? 'READY' : 'IN_PROGRESS',
    detail: client ? undefined : 'Client not set',
  });

  checks.push({
    id: 'link-opportunity',
    group: 'Commercial linkage',
    label: 'Opportunity linked',
    state: opportunityId ? 'READY' : 'IN_PROGRESS',
    detail: opportunityId
      ? undefined
      : 'Optional but required for pursuit / ECLE lineage continuity',
  });

  const due = parseDueDate(dueRaw);
  if (!due) {
    checks.push({
      id: 'schedule-due',
      group: 'Schedule',
      label: 'Due date',
      state: 'IN_PROGRESS',
      detail: 'Due date not set',
    });
  } else if (!isPass && !isFail && due < today) {
    checks.push({
      id: 'schedule-due',
      group: 'Schedule',
      label: 'Due date',
      state: 'BLOCKED',
      detail: 'Past due',
    });
  } else {
    checks.push({
      id: 'schedule-due',
      group: 'Schedule',
      label: 'Due date',
      state: 'READY',
      detail: dueRaw.slice(0, 10),
    });
  }

  if (gate === 'PASSED' || gate === 'NOT_REQUIRED') {
    checks.push({
      id: 'gate',
      group: 'Tender gate',
      label: `Gate: ${gate}`,
      state: 'READY',
      detail: gate === 'NOT_REQUIRED' ? 'Gate not required for tender progression' : undefined,
    });
  } else if (gate === 'WARNING') {
    checks.push({
      id: 'gate',
      group: 'Tender gate',
      label: 'Gate: WARNING',
      state: isPass ? 'READY' : 'IN_PROGRESS',
      detail: isPass ? 'Qualified despite warning' : 'Warning — resolve before relying on tender path',
    });
  } else if (gate === 'BLOCK') {
    checks.push({
      id: 'gate',
      group: 'Tender gate',
      label: 'Gate: BLOCK',
      state: isPass ? 'READY' : 'BLOCKED',
      detail: isPass
        ? 'Block cleared by qualified outcome'
        : 'Tender progression blocked until prequal qualifies or gate changes',
    });
  } else {
    checks.push({
      id: 'gate',
      group: 'Tender gate',
      label: `Gate: ${gate || '—'}`,
      state: 'IN_PROGRESS',
    });
  }

  if (isPass) {
    checks.push({
      id: 'outcome',
      group: 'Outcome',
      label: `Status: ${statusRaw}`,
      state: 'READY',
    });
  } else if (isFail) {
    checks.push({
      id: 'outcome',
      group: 'Outcome',
      label: `Status: ${statusRaw}`,
      state: 'BLOCKED',
      detail: 'Terminal unsuccessful outcome',
    });
  } else {
    checks.push({
      id: 'outcome',
      group: 'Outcome',
      label: `Status: ${statusRaw}`,
      state: 'IN_PROGRESS',
      detail: 'Not yet qualified',
    });
  }

  const groupNames = Array.from(new Set(checks.map((c) => c.group)));
  const groups: PrequalReadinessGroup[] = groupNames.map((name) => {
    const gChecks = checks.filter((c) => c.group === name);
    return { name, state: rollup(gChecks.map((c) => c.state)), checks: gChecks };
  });

  return {
    overall: rollup(checks.map((c) => c.state)),
    checks,
    groups,
  };
}

export function readinessTagColor(state: PrequalReadinessState): string {
  switch (state) {
    case 'READY':
      return 'success';
    case 'BLOCKED':
      return 'error';
    default:
      return 'processing';
  }
}
